import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Slide } from '../../components/deck/Slide';
import { Caption, Eyebrow, INK, Label, Title } from '../../components/deck/Type';
const SERIF = {
  fontFamily: 'var(--font-serif)'
} as const;
const loop = [{
  label: 'More firms',
  tone: INK.blue
}, {
  label: 'More diligence',
  tone: INK.blue
}, {
  label: 'More structured data',
  tone: INK.violet
}, {
  label: 'More feedback',
  tone: INK.gold
}, {
  label: 'Better intelligence',
  tone: INK.emerald
}, {
  label: 'Better workflows',
  tone: INK.emerald
}, {
  label: 'More usage',
  tone: '#F4F7FC'
}];
const compounds = ['Historical company data', 'Firm decisions', 'Research', 'Scoring', 'Feedback', 'Outcomes', 'Benchmarks'];
const R = 38;
function polar(angle: number, radius = R) {
  const rad = angle * Math.PI / 180;
  return {
    x: 50 + Math.cos(rad) * radius,
    y: 50 + Math.sin(rad) * radius
  };
}
export function InstitutionalMemorySlide() {
  const reduced = useReducedMotion();
  const step = 360 / loop.length;
  return <Slide variant="deep" source="Customer-confidential data remains isolated per organisation and is not used to train shared third-party models." data-id="element-267" data-mp="2iaei0yavbhh6">
      <Eyebrow data-id="element-268" data-mp="2r52nopkcv16i">The moat · Institutional data flywheel</Eyebrow>
      <Title className="mt-[2.2cqh] max-w-[62%]" data-id="element-269" data-mp="2me41j9cor8vc">Every workflow makes the intelligence layer stronger.</Title>

      <div className="mt-[2cqh] grid min-h-0 flex-1 grid-cols-[1fr_34%] items-center gap-[4cqw]" data-id="element-270" data-mp="312qhnda504nm">
        <div className="relative mx-auto aspect-square h-full max-h-full" data-id="element-271" data-mp="2w45pm151gra9">
          <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden data-id="element-272" data-mp="2w69scfr7bk8y">
            <circle cx={50} cy={50} r={R} fill="none" stroke="rgba(59,140,255,0.35)" strokeWidth={0.35} data-id="element-273" data-mp="3a1kijfui3h6z" />
            <circle cx={50} cy={50} r={16} fill="rgba(47,190,143,0.07)" stroke="rgba(47,190,143,0.45)" strokeWidth={0.3} data-id="element-274" data-mp="3608s583syrxq" />
            {loop.map((_, i) => {
            const mid = -90 + step * i + step / 2;
            const p = polar(mid);
            return <polygon key={i} points="-1.4,-1.3 1.4,0 -1.4,1.3" fill={INK.blue} transform={`translate(${p.x},${p.y}) rotate(${mid + 90})`} data-id="element-275" data-mp="3audbxfkrcgo5" data-mpi={i} />;
          })}
          </svg>
          <motion.div aria-hidden className="absolute inset-0" animate={reduced ? undefined : {
          rotate: 360
        }} transition={{
          duration: 16,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-276" data-mp="3pr4bppycfuea">
            <span className="absolute h-[1.4cqh] w-[1.4cqh] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2FBE8F]" style={{
            left: '50%',
            top: `${50 - R}%`
          }} data-id="element-277" data-mp="2jk2blf15j2o" />
          </motion.div>

          <div className="absolute inset-[35%] flex flex-col items-center justify-center text-center" data-id="element-278" data-mp="ha9g8vjvlca8">
            <Label tone={INK.emerald} data-id="element-279" data-mp="lblc3itcetqv">Compounds</Label>
            <p className="mt-[0.6cqh] text-[1.15cqw] leading-tight text-[#F4F7FC]" style={SERIF} data-id="element-280" data-mp="lcede49j8r1m">
              Institutional intelligence
            </p>
          </div>

          {loop.map((node, i) => {
          const p = polar(-90 + step * i);
          return <div key={node.label} className="absolute w-[9cqw] -translate-x-1/2 -translate-y-1/2 bg-[#04060C] px-[0.4cqw] py-[0.6cqh] text-center" style={{
            left: `${p.x}%`,
            top: `${p.y}%`
          }} data-id="element-281" data-mp="1wua3ucfqhzps" data-mpi={i}>
                <span className="mx-auto mb-[0.6cqh] block h-[0.8cqh] w-[0.8cqh] rounded-full" style={{
              background: node.tone
            }} data-id="element-282" data-mp="2kitcit2p4eie" />
                <span className="text-[0.95cqw] leading-tight text-[#E3E8F1]" data-id="element-283" data-mp="2frbby64l4fxy">{node.label}</span>
              </div>;
        })}
        </div>

        <div data-id="element-284" data-mp="2ar5shd8gxzqy">
          <Label tone={INK.blue} data-id="element-285" data-mp="388dylku5brvw">What compounds</Label>
          <ul className="mt-[2cqh]" data-id="element-286" data-mp="389ytjbkdhcua">
            {compounds.map(m => <li key={m} className="border-b border-white/8 py-[1.5cqh] text-[1.1cqw] text-[#DCE3EE]" data-id="element-287" data-mp="33gmhs20sl0iy">
                {m}
              </li>)}
          </ul>
          <Caption className="mt-[2.4cqh]" data-id="element-288" data-mp="2s0zp8puhi0da">
            Firm-level intelligence stays within each firm. The platform improves
            through structure, classification and workflow — not pooled client data.
          </Caption>
        </div>
      </div>
    </Slide>;
}